import React, {useState, useCallback, useEffect} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Backdrop,
    Box,
    CircularProgress,
    Drawer,
    IconButton,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import "./kayakBidsViewer.scss";
import {getBiddingDetails, getFilters} from "./api/getMultipliersKayak";
import {enqueueSnackbar} from "notistack";
import HotelGroupSelector from "./component/HotelGroupMarketSelector";
import MultipliersTabsKayak from "./component/MultipliersTabsKayak";
import {multipliersMenuKayak} from "./constant/multipliersMenuKayak";
import KayakTable from "./component/KayakTable";
import KayakTablePagination from "./component/KayakTable/KayakTablePagination";
import {deleteBiddingRows, deleteKayakBidding} from "./api/deleteKayakBidding";
import {getBodyComponent, getHeaderComponent} from "./component/KayakTable/TableComponents";
const KayakBidsViewer = ({ isOpen, onClose, idAccount, platform }) => {
    const [data, setData] = useState({
        groups_list: [],
        hotels_list: [],
        markets_list:[],
        can_use_group: false,
        groups_multipliers: {
            extra_bid:  [],
            max_bid: [],
            weekend_stay_multipliers: [],
            days_to_arrival_multipliers: [],
            length_of_stay_multipliers: [],
            market_bids: [],
        },
        hotels_multipliers: {
            extra_bid: [],
            max_bid: [],
            weekend_stay_multipliers: [],
            days_to_arrival_multipliers: [],
            length_of_stay_multipliers: [],
            market_bids: [],
        }
    });

    // Loader state
    const [loading, setLoading] = useState({
        filter: false,
        multipliersMenu: {
            extra_bid: false,
            max_bid: false,
            market_bids: false,
            weekend_stay_multipliers: false,
            days_to_arrival_multipliers: false,
            length_of_stay_multipliers: false
        }
    });

    // Tabs Selector
    const [selectedTab, setSelectedTab] = useState(multipliersMenuKayak[0]?.id);

    // Hotels Selector
    const [selectedItem, setSelectedItem] = useState([]);
    const [selectedType, setSelectedType] = useState("hotels");
    const [selectedCategory, setSelectedCategory] = useState("hotels");
    const [searchQuery, setSearchQuery] = useState("");

    const updateData = (category, type, result) => {
        setData(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [category]: result,
            }
        }));

        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [category]: false
            }
        }));
    };

    const fetchMultipliersData = useCallback(async () => {
        setLoading(prev => ({
            ...prev,
            filter: true,
            multipliersMenu: {
                extra_bid: true,
                max_bid: true,
                weekend_stay_multipliers: true,
                days_to_arrival_multipliers: true,
                length_of_stay_multipliers: true,
                market_bids: true
            }
        }));

        try {
            getBiddingDetails(idAccount, "extra_bid").then(res => {
                updateData("extra_bid", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("extra_bid", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getBiddingDetails(idAccount, "max_bid").then(res => {
                updateData("max_bid", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("max_bid", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getBiddingDetails(idAccount, "weekend_stay").then(res => {
                updateData("weekend_stay_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("weekend_stay_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getBiddingDetails(idAccount, "days_to_arrival").then(res => {
                updateData("days_to_arrival_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("days_to_arrival_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getBiddingDetails(idAccount, "length_of_stay").then(res => {
                updateData("length_of_stay_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("length_of_stay_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getBiddingDetails(idAccount, "market_bids").then(res => {
                updateData("market_bids", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("market_bids", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            // Filters
            const filtersResult = await getFilters(idAccount, platform);
            setData(prev => ({
                ...prev,
                groups_list: filtersResult.data?.groups_list ?? [],
                hotels_list: filtersResult.data?.hotels_list ?? [],
                markets_list: filtersResult.data?.markets_list ?? [],
                can_use_group: filtersResult.data?.can_use_group ?? false
            }));
            setLoading(prev => ({ ...prev, filter: false }));
        } catch (error) {
            enqueueSnackbar('Error while fetching multipliers:', { variant: 'error' });
        } finally {
            setLoading({
                filter: false,
                multipliersMenu: {
                    extra_bid: false,
                    max_bid: false,
                    market_bids: false,
                    weekend_stay_multipliers: false,
                    days_to_arrival_multipliers: false,
                    length_of_stay_multipliers: false
                }
            });
        }
    }, [idAccount, platform]);

    useEffect(() => {
        if (isOpen) {
            fetchMultipliersData();
        }
    }, [isOpen, fetchMultipliersData]);

    // Accordion State
    const [expanded, setExpanded] = useState(true);
    const handleAccordionToggle = () => setExpanded(!expanded);

    // Pagination state
    const [page, setPage] = useState(0);

    // Set pagination
    const rowsPerPage = 10;
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Sorting and Filtering state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [filters, setFilters] = useState({});

    const handleSortChange = useCallback((config) => {
        setSortConfig(config);
    }, []);

    const handleFilterChange = useCallback((key, filter) => {
        setFilters((prev) => ({
            ...prev,
            [key]: filter
        }));
        setPage(0); // Reset page when filtering
    }, []);

    // Apply filters
    const baseData = selectedType === "hotels"
        ? data.hotels_multipliers[selectedTab] ?? []
        : data.groups_multipliers[selectedTab] ?? [];

    // First filter: by selectedItem (i.e., selected hotels or groups)
    const selectedIds = selectedItem.map(item => item.id);
    const selectedIdSet = new Set(selectedIds); // more efficient

    const filteredBySelection = selectedItem.length > 0
        ? baseData.filter(row => selectedIdSet.has(Number(row.entity_id)))
        : baseData;

    // Second filter: by column filters (text or number filters)
    const filteredData = filteredBySelection.filter((row) => {
        return Object.entries(filters).every(([key, filter]) => {
            const rowValue = row[key];

            if (rowValue === undefined || filter === "" || filter === null) return true;

            // Multi-select filter (like devices)
            if (Array.isArray(filter)) {
                return filter.length === 0 || filter.includes(rowValue);
            }

            // Text filter
            if (typeof filter === "string") {
                return rowValue.toString().toLowerCase().includes(filter.toLowerCase());
            }

            // Number filter
            const { operator, value } = filter;
            const numValue = Number(value);
            const rowNum = Number(rowValue);

            if (isNaN(numValue) || isNaN(rowNum)) return true;

            switch (operator) {
                case ">": return rowNum > numValue;
                case "<": return rowNum < numValue;
                case "=": return rowNum === numValue;
                default: return true;
            }
        });
    });

    // Apply sorting
    const sortedData = [...filteredData].sort((a, b) => {
        const { key, direction } = sortConfig;
        if (!key) return 0;

        let aValue = a[key];
        let bValue = b[key];

        if (typeof aValue === "number" && typeof bValue === "number") {
            return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        return direction === "asc"
            ? aValue.toString().localeCompare(bValue.toString())
            : bValue.toString().localeCompare(aValue.toString());
    });

    // Determine correct multipliers data based on selection
    const tableData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    // Checkbox Selector
    const [selectedRows, setSelectedRows] = useState([]);
    const currentPageRowIds = tableData.map((r) => r.id);
    const allSelected = currentPageRowIds.every(id => selectedRows.includes(id));
    const someSelected = currentPageRowIds.some(id => selectedRows.includes(id)) && !allSelected;

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const newSelections = [...new Set([...selectedRows, ...currentPageRowIds])];
            setSelectedRows(newSelections);
        } else {
            const newSelections = selectedRows.filter(id => !currentPageRowIds.includes(id));
            setSelectedRows(newSelections);
        }
    };

    // Multipliers menu
    const handleTabChange = (_, newValue) => {
        setFilters({});
        setSelectedRows([]);
        setSortConfig({ key: null, direction: "asc" });
        setPage(0);
        setSelectedTab(newValue);
    };

    // update function
    const updateMultiplierRow = () => {
        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: true
            }
        }));

        try {
            switch (selectedTab) {
                case "extra_bid":
                    getBiddingDetails(idAccount, "extra_bid").then(res => {
                        updateData("extra_bid", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("extra_bid", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
                case "max_bid":
                    getBiddingDetails(idAccount, "max_bid").then(res => {
                        updateData("max_bid", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("max_bid", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
                case "market_bids":
                    getBiddingDetails(idAccount, "market_bids").then(res => {
                        updateData("market_bids", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("market_bids", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
                case "weekend_stay_multipliers":
                    getBiddingDetails(idAccount, "weekend_stay").then(res => {
                        updateData("weekend_stay_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("weekend_stay_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
                case "days_to_arrival_multipliers":
                    getBiddingDetails(idAccount, "days_to_arrival").then(res => {
                        updateData("days_to_arrival_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("days_to_arrival_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
                case "length_of_stay_multipliers":
                    getBiddingDetails(idAccount, "length_of_stay").then(res => {
                        updateData("length_of_stay_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                        updateData("length_of_stay_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
                    });
            }
        } catch (error) {
            enqueueSnackbar('Error while fetching multipliers:', { variant: 'error' });
        } finally {
            setLoading(prev => ({
                ...prev,
                multipliersMenu: {
                    ...prev.multipliersMenu,
                    [selectedTab]: false
                }
            }));
        }
    };

    const deleteSelectedRows = async () => {
        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: true
            }
        }));

        try {
            const response = await deleteBiddingRows(idAccount, selectedTab, selectedRows, selectedType);

            if (response?.data?.success) {
                setData(prevData => {
                    const target = selectedType === "hotels"
                        ? prevData.hotels_multipliers
                        : prevData.groups_multipliers;

                    const updatedArray = target[selectedTab].filter(
                        row => !selectedRows.includes(row.id)
                    );

                    const updatedMultipliers = {
                        ...target,
                        [selectedTab]: updatedArray
                    };

                    return selectedType === "hotels"
                        ? { ...prevData, hotels_multipliers: updatedMultipliers }
                        : { ...prevData, groups_multipliers: updatedMultipliers };
                });

                enqueueSnackbar("Selected rows deleted successfully.", { variant: "success" });
                setSelectedRows([]);
            } else {
                enqueueSnackbar("Failed to delete selected rows.", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Failed to delete selected rows.", { variant: "error" });
        }

        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: false
            }
        }));
    };

    const deleteSingleRow = async (rowId) => {
        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: true
            }
        }));

        try {
            const response = await deleteKayakBidding({
                accountId: idAccount,
                tabType: selectedTab,
                idMultiplier: rowId,
                hotelsOrGroup: selectedType
            });

            if (response?.data?.success) {
                setData(prevData => {
                    const target = selectedType === "hotels"
                        ? prevData.hotels_multipliers
                        : prevData.groups_multipliers;

                    const updatedArray = target[selectedTab].filter(row => row.id !== rowId);

                    const updatedMultipliers = {
                        ...target,
                        [selectedTab]: updatedArray
                    };

                    return selectedType === "hotels"
                        ? { ...prevData, hotels_multipliers: updatedMultipliers }
                        : { ...prevData, groups_multipliers: updatedMultipliers };
                });

                setSelectedRows(prev => prev.filter(id => id !== rowId));
            } else {
                enqueueSnackbar("Failed to delete row.", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Failed to delete row.", { variant: "error" });
        }

        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: false
            }
        }));
    };

    return (
        <Drawer anchor="right" open={isOpen} onClose={onClose}>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading.filter}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box className={`kayakBidsViewerPanel ${isOpen ? "open" : ""}`} style={{ padding: 16, width: "900px" }}>
                {/* Header */}
                <Box className="header">
                    <Box style={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={onClose} className="closeButton">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body1" className="title">KAYAK Bidding</Typography>
                    </Box>
                </Box>

                <Box className="scrollable-content">
                    {/* Accordion */}
                    <Accordion expanded={expanded} onChange={handleAccordionToggle} className="multipliers-accordion">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-summary">
                            <Typography fontWeight="bold">Bidding</Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-content">
                            <HotelGroupSelector
                                data={data}
                                setSelectedCategory={setSelectedCategory}
                                selectedItem={selectedItem}
                                setSelectedItem={setSelectedItem}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                selectedType={selectedType}
                                setSelectedType={setSelectedType}
                                setPage={setPage}
                            />

                            {/* Tabs */}
                            <MultipliersTabsKayak selectedTab={selectedTab} handleTabChange={handleTabChange} />

                            <KayakTable
                                loading={loading}
                                selectedTab={selectedTab}
                                headerComponent={getHeaderComponent({
                                    selectedTab,
                                    selectedType,
                                    allSelected,
                                    someSelected,
                                    handleSelectAll,
                                    sortConfig,
                                    handleSortChange,
                                    filters,
                                    handleFilterChange,
                                    baseData,
                                    filteredData,
                                })}
                                bodyComponent={getBodyComponent({
                                    selectedTab,
                                    tableData,
                                    selectedRows,
                                    setSelectedRows,
                                    updateMultiplierRow,
                                    deleteSingleRow,
                                })}
                            />

                            <KayakTablePagination
                                page={page}
                                totalItems={filteredData.length}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                onBulkDelete={deleteSelectedRows}
                                onClearFilters={() => {
                                    setFilters({});
                                    setPage(0);
                                    setSelectedItem([]);
                                }}
                                isFiltersDisable={filters.length === 0}
                                disableDelete={selectedRows.length === 0}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        </Drawer>
    );
};

export default KayakBidsViewer;